import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Autocomplete,
  MenuItem,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { clientsApi, Client, CreateClientData, ClientContact } from '../services/clients';
import { lookupAddressByPostcode, formatSortCode, getLastWorkingDaysForYear } from '../utils/addressValidation';
import { countries, getCurrencyByCountry } from '../utils/countries';
import { format, parseISO } from 'date-fns';
import InvoiceDateCalendar from '../components/InvoiceDateCalendar';
import { useCompany } from '../contexts/CompanyContext';

// Form data type - allows Date[] for dates in the form
type ClientFormData = Omit<CreateClientData, 'invoiceDates' | 'timesheetDates'> & {
  invoiceDates?: Date[];
  timesheetDates?: Date[];
};

const Clients: React.FC = () => {
  const { baseCurrency } = useCompany();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [validatingAddress, setValidatingAddress] = useState(false);
  const [addressLookupError, setAddressLookupError] = useState<string | null>(null);

  const { register, handleSubmit, control, reset, watch, setValue, formState: { errors } } = useForm<ClientFormData>({
    defaultValues: {
      name: '',
      billingAddress: {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: '',
      },
      chargeRate: 0,
      currency: baseCurrency,
      workingHoursPerDay: 8,
      contacts: [{ name: '', email: '', phone: '', role: '' }],
      timesheetSubmissionDate: undefined,
      invoiceSubmissionDate: undefined,
      invoiceDates: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contacts',
  });

  const watchedPostalCode = watch('billingAddress.postalCode');
  const watchedCountry = watch('billingAddress.country');
  const watchedSortCode = watch('accountDetails.sortCode');

  // Handle country change to auto-set currency
  const handleCountryChange = (countryCode: string) => {
    setValue('billingAddress.country', countryCode);
    const currency = getCurrencyByCountry(countryCode);
    setValue('currency', currency);
  };

  // Generate last working days for the next year
  const currentYear = new Date().getFullYear();
  const lastWorkingDays = getLastWorkingDaysForYear(currentYear);
  const nextYearDays = getLastWorkingDaysForYear(currentYear + 1);
  const allLastWorkingDays = [...lastWorkingDays, ...nextYearDays];

  useEffect(() => {
    loadClients();
  }, []);

  // Handle address lookup when postcode changes
  useEffect(() => {
    const timeoutId = setTimeout(async () => {
      if (watchedPostalCode && watchedPostalCode.length >= 5 && watchedCountry) {
        setValidatingAddress(true);
        setAddressLookupError(null);
        try {
          const result = await lookupAddressByPostcode(watchedPostalCode, watchedCountry);
          if (result.valid && result.city) {
            setValue('billingAddress.city', result.city);
            if (result.street && !watch('billingAddress.street')) {
              setValue('billingAddress.street', result.street);
            }
            if (result.county && !watch('billingAddress.state')) {
              setValue('billingAddress.state', result.county);
            }
          }
        } catch (err) {
          setAddressLookupError('Could not validate address. Please enter manually.');
        } finally {
          setValidatingAddress(false);
        }
      }
    }, 1000); // Debounce for 1 second

    return () => clearTimeout(timeoutId);
  }, [watchedPostalCode, watchedCountry, setValue, watch]);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await clientsApi.getAll();
      setClients(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (client?: Client) => {
    if (client) {
      setEditingClient(client);
      reset({
        name: client.name,
        billingAddress: client.billingAddress,
        accountDetails: client.accountDetails,
        chargeRate: client.chargeRate,
        currency: client.currency,
        workingHoursPerDay: client.workingHoursPerDay || 8,
        contacts: client.contacts.length > 0 ? client.contacts : [{ name: '', email: '', phone: '', role: '' }],
        timesheetSubmissionDate: client.timesheetSubmissionDate,
        timesheetDates: client.timesheetDates && Array.isArray(client.timesheetDates)
          ? (client.timesheetDates as string[]).map((d: string) => {
              try {
                return parseISO(d);
              } catch {
                return new Date(d);
              }
            }).filter((d): d is Date => d instanceof Date)
          : [],
        invoiceSubmissionDate: client.invoiceSubmissionDate,
        invoiceDates: client.invoiceDates && Array.isArray(client.invoiceDates)
          ? (client.invoiceDates as string[]).map((d: string) => {
              try {
                return parseISO(d);
              } catch {
                return new Date(d);
              }
            }).filter((d): d is Date => d instanceof Date)
          : [],
      });
    } else {
      setEditingClient(null);
      reset({
        name: '',
        billingAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: '',
        },
        chargeRate: 0,
        currency: baseCurrency,
        workingHoursPerDay: 8,
        contacts: [{ name: '', email: '', phone: '', role: '' }],
        timesheetSubmissionDate: undefined,
        timesheetDates: [],
        invoiceSubmissionDate: undefined,
        invoiceDates: [],
      });
    }
    setOpenDialog(true);
    setAddressLookupError(null);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingClient(null);
    reset();
    setAddressLookupError(null);
  };

  const onSubmit = async (data: ClientFormData) => {
    try {
      setError(null);
      // Convert Date objects to ISO strings for API
      const submitData: CreateClientData = {
        ...data,
        timesheetDates: data.timesheetDates?.map((d: any) => {
          if (d instanceof Date) {
            return format(d, 'yyyy-MM-dd');
          }
          return d;
        }) || [],
        invoiceDates: data.invoiceDates?.map((d: any) => {
          if (d instanceof Date) {
            return format(d, 'yyyy-MM-dd');
          }
          return d;
        }) || [],
      };
      if (editingClient) {
        await clientsApi.update(editingClient.id, submitData);
      } else {
        await clientsApi.create(submitData);
      }
      handleCloseDialog();
      loadClients();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save client');
    }
  };

  const handleDeleteClick = (client: Client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (clientToDelete) {
      try {
        setError(null);
        await clientsApi.delete(clientToDelete.id);
        setDeleteDialogOpen(false);
        setClientToDelete(null);
        loadClients();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to delete client');
      }
    }
  };

  const handleSortCodeChange = (value: string) => {
    const formatted = formatSortCode(value);
    setValue('accountDetails.sortCode', formatted);
  };

  // Removed handleLastWorkingDaySelect - no longer needed

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Clients</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          Add Client
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Billing Address</TableCell>
              <TableCell>Charge Rate</TableCell>
              <TableCell>Currency</TableCell>
              <TableCell>Contacts</TableCell>
              <TableCell>Timesheet Date</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">No clients found. Add your first client to get started.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell>{client.name}</TableCell>
                  <TableCell>
                    {client.billingAddress.street}, {client.billingAddress.city}, {client.billingAddress.postalCode}
                  </TableCell>
                  <TableCell>{client.chargeRate.toFixed(2)}</TableCell>
                  <TableCell>{client.currency}</TableCell>
                  <TableCell>
                    {client.contacts.map((contact, idx) => (
                      <Chip key={idx} label={contact.name} size="small" sx={{ mr: 0.5 }} />
                    ))}
                  </TableCell>
                  <TableCell>
                    {client.timesheetDates && client.timesheetDates.length > 0
                      ? `${client.timesheetDates.length} date(s)`
                      : client.timesheetSubmissionDate
                      ? `Day ${client.timesheetSubmissionDate}`
                      : 'Not set'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleOpenDialog(client)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteClick(client)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>{editingClient ? 'Edit Client' : 'Add New Client'}</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Client Name"
                  {...register('name', { required: 'Client name is required' })}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Billing Address
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Street Address"
                  {...register('billingAddress.street', { required: 'Street address is required' })}
                  error={!!errors.billingAddress?.street}
                  helperText={errors.billingAddress?.street?.message}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  {...register('billingAddress.postalCode', { required: 'Postal code is required' })}
                  error={!!errors.billingAddress?.postalCode}
                  helperText={
                    validatingAddress
                      ? 'Validating address...'
                      : addressLookupError || errors.billingAddress?.postalCode?.message || 
                        (watchedCountry?.toUpperCase() === 'GB' || watchedCountry?.toUpperCase() === 'UK' 
                          ? 'UK postcodes will be validated automatically' 
                          : '')
                  }
                  InputProps={{
                    endAdornment: validatingAddress ? <CircularProgress size={20} /> : null,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  {...register('billingAddress.city', { required: 'City is required' })}
                  error={!!errors.billingAddress?.city}
                  helperText={errors.billingAddress?.city?.message}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="State/Province"
                  {...register('billingAddress.state')}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Controller
                  name="billingAddress.country"
                  control={control}
                  rules={{ required: 'Country is required' }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      select
                      fullWidth
                      label="Country"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        handleCountryChange(e.target.value);
                      }}
                    >
                      {countries.map((country) => (
                        <MenuItem key={country.code} value={country.code}>
                          {country.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Account Details (Optional)
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="accountDetails.accountNumber"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!value) return true; // Optional field
                      const digitsOnly = value.replace(/\D/g, '');
                      if (digitsOnly.length !== 8) {
                        return 'Account number must be exactly 8 digits';
                      }
                      return true;
                    },
                  }}
                  render={({ field, fieldState }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Account Number"
                      error={!!fieldState.error}
                      helperText={fieldState.error?.message || 'Must be exactly 8 digits'}
                      inputProps={{ maxLength: 8 }}
                      onChange={(e) => {
                        // Only allow digits
                        const digitsOnly = e.target.value.replace(/\D/g, '');
                        field.onChange(digitsOnly);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Controller
                  name="accountDetails.sortCode"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Sort Code"
                      placeholder="00-00-00"
                      inputProps={{ maxLength: 8 }}
                      onChange={(e) => {
                        const formatted = formatSortCode(e.target.value);
                        field.onChange(formatted);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="IBAN"
                  {...register('accountDetails.iban')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="SWIFT"
                  {...register('accountDetails.swift')}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="number"
                  label="Charge Rate"
                  {...register('chargeRate', {
                    required: 'Charge rate is required',
                    valueAsNumber: true,
                    min: { value: 0, message: 'Charge rate must be positive' },
                  })}
                  error={!!errors.chargeRate}
                  helperText={errors.chargeRate?.message}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="Working Hours per Day"
                  inputProps={{ step: 0.5, min: 0.5, max: 24 }}
                  {...register('workingHoursPerDay', {
                    valueAsNumber: true,
                    min: { value: 0.5, message: 'Must be at least 0.5 hours' },
                    max: { value: 24, message: 'Cannot exceed 24 hours' },
                  })}
                  error={!!errors.workingHoursPerDay}
                  helperText={errors.workingHoursPerDay?.message || 'Used to calculate days from hours in invoices (default: 8)'}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  label="Currency"
                  {...register('currency', { required: 'Currency is required' })}
                  error={!!errors.currency}
                  helperText={errors.currency?.message}
                  inputProps={{ maxLength: 3 }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Invoice Dates (Select dates when invoices should be generated)
                </Typography>
                <Controller
                  name="invoiceDates"
                  control={control}
                  render={({ field }) => (
                    <InvoiceDateCalendar
                      selectedDates={field.value || []}
                      onChange={(dates) => field.onChange(dates)}
                      minDate={new Date()}
                      maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Timesheet Submission Dates (Select dates when timesheets are due)
                </Typography>
                <Controller
                  name="timesheetDates"
                  control={control}
                  render={({ field }) => {
                    const invoiceDates = watch('invoiceDates') || [];
                    return (
                      <InvoiceDateCalendar
                        selectedDates={field.value || []}
                        onChange={(dates) => field.onChange(dates)}
                        minDate={new Date()}
                        maxDate={new Date(new Date().setFullYear(new Date().getFullYear() + 1))}
                        referenceDates={invoiceDates}
                        referenceLabel="Invoice Dates"
                      />
                    );
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle2">Contacts</Typography>
                  <Button size="small" onClick={() => append({ name: '', email: '', phone: '', role: '' })}>
                    Add Contact
                  </Button>
                </Box>
                {fields.map((field, index) => (
                  <Grid container spacing={2} key={field.id} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Name"
                        {...register(`contacts.${index}.name`, { required: 'Contact name is required' })}
                        error={!!errors.contacts?.[index]?.name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        {...register(`contacts.${index}.email`, {
                          required: 'Email is required',
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Invalid email address',
                          },
                        })}
                        error={!!errors.contacts?.[index]?.email}
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <TextField
                        fullWidth
                        label="Phone"
                        {...register(`contacts.${index}.phone`)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                      {fields.length > 1 && (
                        <IconButton onClick={() => remove(index)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingClient ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Client</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>{clientToDelete?.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Clients;
